/**
 * Sparse-data decisions for MetricChart, kept pure so the bucket/render choices
 * are unit-tested without mounting ECharts.
 *
 * A metric logged occasionally (weight, work hours, an odd body-fat reading)
 * renders as an empty-looking line: a fine bucket returns a handful of scattered
 * points, symbols are hidden, and a year-long x-axis strands the cluster in one
 * corner. These helpers detect that and drive the fixes — coarser bucket,
 * visible symbols, and an x-axis clamped to where the data actually is.
 */

import type { MetricBucket } from '../api.js';

const DAY_MS = 86_400_000;

/** Below this weekly density a series is treated as sparse. */
export const SPARSE_PER_WEEK = 2;

/** Fewer than this many points always draws symbols, even when not sparse. */
export const ALWAYS_SYMBOLS_BELOW = 60;

/** Average points per week for `count` points spread over `spanDays`. */
export function avgPointsPerWeek(count: number, spanDays: number): number {
  const weeks = Math.max(spanDays, 1) / 7;
  return count / weeks;
}

/** A series is sparse when it averages fewer than 2 points/week. */
export function isSparse(count: number, spanDays: number): boolean {
  return count > 0 && avgPointsPerWeek(count, spanDays) < SPARSE_PER_WEEK;
}

/**
 * The bucket to actually fetch at. A finer-than-day bucket that comes back
 * sparse gains nothing from its granularity, so fall back to `day`. `day` (and
 * an empty/dense series) is left untouched, so this can only downgrade once.
 */
export function effectiveBucket(
  requested: MetricBucket,
  count: number,
  spanDays: number,
): MetricBucket {
  if (requested !== 'day' && isSparse(count, spanDays)) return 'day';
  return requested;
}

/** Whether the whole chart should downgrade to a day bucket (any series sparse). */
export function shouldFallBackToDay(
  requested: MetricBucket,
  counts: number[],
  spanDays: number,
): boolean {
  if (requested === 'day') return false;
  return counts.some((c) => isSparse(c, spanDays));
}

export interface SeriesRenderMode {
  /** Draw a symbol at every point (sparse series, or short series < 60 points). */
  showSymbols: boolean;
  /** Bridge the line across gaps so a sparse trend still reads as a line. */
  connectNulls: boolean;
}

/** Per-series rendering treatment given its point count over the span. */
export function seriesRenderMode(count: number, spanDays: number): SeriesRenderMode {
  const sparse = isSparse(count, spanDays);
  return {
    showSymbols: sparse || (count > 0 && count < ALWAYS_SYMBOLS_BELOW),
    connectNulls: sparse,
  };
}

/**
 * Earliest and latest point timestamps (ms) across every series, or `null` when
 * there are no points. Used to clamp the x-axis to the real data extent.
 */
export function dataExtent(seriesTimes: string[][]): { minMs: number; maxMs: number } | null {
  let minMs = Infinity;
  let maxMs = -Infinity;
  for (const times of seriesTimes) {
    for (const t of times) {
      const ms = new Date(t).getTime();
      if (!Number.isFinite(ms)) continue;
      if (ms < minMs) minMs = ms;
      if (ms > maxMs) maxMs = ms;
    }
  }
  if (minMs === Infinity) return null;
  return { minMs, maxMs };
}

/**
 * Whether the data extent starts meaningfully later than the requested range
 * (more than a day in), so the chart should note that it trimmed the x-axis.
 */
export function isTrimmedStart(extentMinMs: number, requestedFromMs: number): boolean {
  return extentMinMs - requestedFromMs > DAY_MS;
}
